import React, { useState, useEffect } from "react";
import { Filter, X, Calendar } from "lucide-react";
import { getTemplates } from "../apis/template";

const CampaignFilter = ({ 
  filters, 
  onFilterChange, 
  onApplyFilter,
  templates = [],
  whatsappAccounts = [],
  loading = false 
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || "",
    campaign_name: filters.campaign_name || "",
    template_id: filters.template_id || "",
    whatsapp_account_id: filters.whatsapp_account_id || "",
    date_from: filters.date_from || "",
    date_to: filters.date_to || "",
  });

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters({
      status: filters.status || "",
      campaign_name: filters.campaign_name || "",
      template_id: filters.template_id || "",
      whatsapp_account_id: filters.whatsapp_account_id || "",
      date_from: filters.date_from || "",
      date_to: filters.date_to || "",
    });
  }, [filters]);

  const handleInputChange = (field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilter = () => {
    // Update parent state first
    onFilterChange(localFilters);
    // Pass filters directly to apply handler to avoid stale state
    onApplyFilter(localFilters);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: "",
      campaign_name: "",
      template_id: "",
      whatsapp_account_id: "",
      date_from: "",
      date_to: "",
    };
    setLocalFilters(clearedFilters);
    // Update parent state
    onFilterChange(clearedFilters);
    // Pass cleared filters directly to apply handler
    onApplyFilter(clearedFilters);
    setShowFilters(false);
  };

  const hasActiveFilters = Object.values(localFilters).some(
    (value) => value !== "" && value !== null
  );

  return (
    <div className="mb-4">
      {/* Filter Toggle Button */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
            hasActiveFilters
              ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
              : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
          }`}
        >
          <Filter size={14} />
          Filters
          {hasActiveFilters && (
            <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white text-[10px] rounded-full">
              {Object.values(localFilters).filter((v) => v !== "" && v !== null).length}
            </span>
          )}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Campaign Name Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Campaign Name
              </label>
              <input
                type="text"
                value={localFilters.campaign_name}
                onChange={(e) => handleInputChange("campaign_name", e.target.value)}
                placeholder="Search campaign name..."
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Status
              </label>
              <select
                value={localFilters.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
                <option value="aborted">Aborted</option>
              </select>
            </div>

            {/* Template Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Template
              </label>
              <select
                value={localFilters.template_id}
                onChange={(e) => handleInputChange("template_id", e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                disabled={loading}
              >
                <option value="">All Templates</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            {/* WhatsApp Account Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                WhatsApp Account
              </label>
              <select
                value={localFilters.whatsapp_account_id}
                onChange={(e) => handleInputChange("whatsapp_account_id", e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">All Accounts</option>
                {whatsappAccounts.map((account) => (
                  <option key={account.id} value={account.phone_id || account.id}>
                    {account.phone_number} ({account.business_name || account.waba_name || "N/A"})
                  </option>
                ))}
              </select>
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                <Calendar size={12} className="inline mr-1" />
                Date From
              </label>
              <input
                type="date"
                value={localFilters.date_from}
                onChange={(e) => handleInputChange("date_from", e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Date To Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                <Calendar size={12} className="inline mr-1" />
                Date To
              </label>
              <input
                type="date"
                value={localFilters.date_to}
                onChange={(e) => handleInputChange("date_to", e.target.value)}
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleClearFilters}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={handleApplyFilter}
              className="px-4 py-1.5 text-xs font-medium text-white bg-[#25D366] rounded-lg hover:bg-[#20BD5A] transition-colors flex items-center gap-1.5"
            >
              <Filter size={14} />
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignFilter;

